import { configureRequestParams, getErrorMessage } from '@/utils/helpers'

import axios from './instance'

// ==================== Admin: Global Certification Categories ====================

/**
 * Gets all external certification categories (admin view).
 */
export const getAllExternalCertificationCategories = async ({
  sorting,
  pagination,
  searchTerm,
  filters,
}) => {
  const params = configureRequestParams(sorting, pagination, searchTerm, filters)
  try {
    return await axios.get(`/external-certification-categories/${params}&expand=entity`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Creates a new global external certification category.
 */
export const createGlobalExternalCertificationCategory = async (payload) => {
  try {
    return await axios.post('/external-certification-categories/', payload)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Updates a global external certification category.
 */
export const updateGlobalExternalCertificationCategory = async (id, payload) => {
  try {
    return await axios.patch(`/external-certification-categories/${id}/`, payload)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Deletes a global external certification category.
 */
export const deleteGlobalExternalCertificationCategory = async (id) => {
  try {
    return await axios.delete(`/external-certification-categories/${id}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

// ==================== External Certification Categories (Entity-scoped) ====================

/**
 * Gets the list of external certification categories for an entity.
 */
export const getExternalCertificationCategories = async ({
  entityId,
  sorting,
  pagination,
  searchTerm,
  filters,
}) => {
  const params = configureRequestParams(sorting, pagination, searchTerm, filters)
  try {
    return await axios.get(`/entities/${entityId}/external-certification-categories/${params}`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Gets a single external certification category by ID.
 */
export const getExternalCertificationCategory = async (entityId, id) => {
  try {
    return await axios.get(`/entities/${entityId}/external-certification-categories/${id}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Creates a new external certification category.
 */
export const createExternalCertificationCategory = async (entityId, payload) => {
  try {
    return await axios.post(`/entities/${entityId}/external-certification-categories/`, payload)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Updates an external certification category.
 */
export const updateExternalCertificationCategory = async (entityId, id, payload) => {
  try {
    return await axios.patch(
      `/entities/${entityId}/external-certification-categories/${id}/`,
      payload,
    )
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Deletes an external certification category.
 */
export const deleteExternalCertificationCategory = async (entityId, id) => {
  try {
    return await axios.delete(`/entities/${entityId}/external-certification-categories/${id}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

// ==================== User External Certifications ====================

/**
 * Gets the list of user external certifications for an entity.
 */
export const getUserExternalCertifications = async ({
  entityId,
  sorting,
  pagination,
  searchTerm,
  filters,
  userId,
}) => {
  const combinedFilters = { ...filters }
  if (userId) combinedFilters.user = userId
  const params = configureRequestParams(sorting, pagination, searchTerm, combinedFilters)
  try {
    return await axios.get(
      `/entities/${entityId}/external-certifications/${params}&expand=user,category,approved_by,files,courses`,
    )
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Gets a single user external certification by ID.
 */
export const getUserExternalCertification = async (entityId, id) => {
  try {
    return await axios.get(
      `/entities/${entityId}/external-certifications/${id}/?expand=user,category,approved_by,files,courses`,
    )
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Creates a new user external certification submission.
 */
export const createUserExternalCertification = async (entityId, payload) => {
  try {
    return await axios.post(`/entities/${entityId}/external-certifications/`, payload)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Updates a user external certification.
 */
export const updateUserExternalCertification = async (entityId, id, payload) => {
  try {
    return await axios.patch(`/entities/${entityId}/external-certifications/${id}/`, payload)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Deletes a user external certification.
 */
export const deleteUserExternalCertification = async (entityId, id) => {
  try {
    return await axios.delete(`/entities/${entityId}/external-certifications/${id}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Approves a user external certification.
 */
export const approveUserExternalCertification = async (entityId, id) => {
  try {
    return await axios.post(`/entities/${entityId}/external-certifications/${id}/approve/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Rejects a user external certification.
 */
export const rejectUserExternalCertification = async (entityId, id, rejectionReason = '') => {
  try {
    return await axios.post(`/entities/${entityId}/external-certifications/${id}/reject/`, {
      rejectionReason,
    })
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Batch approves multiple user external certifications.
 */
export const batchApproveUserExternalCertifications = async (entityId, certificationIds) => {
  try {
    return await axios.post(`/entities/${entityId}/external-certifications/batch_approve/`, {
      certificationIds,
    })
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}
